import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import Header from '../layout/Header';
import Footer from '../sections/Footer';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiPlay,FiTv,FiClock,FiCalendar,FiRadio,FiRefreshCw,FiEye,FiLive,FiMonitor,FiImage}=FiIcons;

const LiveTVPage=()=> {
  const [schedule,setSchedule]=useState([]);
  const [loading,setLoading]=useState(true);
  const [currentProgram,setCurrentProgram]=useState(null);
  const [nextProgram,setNextProgram]=useState(null);
  const [error,setError]=useState(null);
  const [progress,setProgress]=useState(0);
  const [now,setNow]=useState(new Date());

  // Parse XMLTV data
  const parseXMLTV=(xmlString)=> {
    try {
      const parser=new DOMParser();
      const xmlDoc=parser.parseFromString(xmlString,'text/xml');
      const programmes=xmlDoc.getElementsByTagName('programme');
      const parsedSchedule=[];

      for (let i=0;i < programmes.length;i++) {
        const programme=programmes[i];
        const startTime=new Date(programme.getAttribute('start'));
        const stopTime=new Date(programme.getAttribute('stop'));
        const titleElement=programme.getElementsByTagName('title')[0];
        const descElement=programme.getElementsByTagName('desc')[0];

        // Find icon (thumbnail) element
        let iconUrl='';
        const iconElements=programme.getElementsByTagName('icon');
        if (iconElements.length > 0) {
          iconUrl=iconElements[0].getAttribute('src');
        }

        parsedSchedule.push({
          id: i,
          title: titleElement ? titleElement.textContent : 'Unknown Program',
          description: descElement ? descElement.textContent : '',
          startTime,
          stopTime,
          duration: Math.round((stopTime - startTime) / (1000 * 60)),// duration in minutes
          iconUrl: iconUrl
        });
      }

      return parsedSchedule.sort((a,b)=> a.startTime - b.startTime);
    } catch (err) {
      console.error('Error parsing XMLTV:',err);
      return [];
    }
  };

  // Fetch schedule data
  const fetchSchedule=async ()=> {
    try {
      setLoading(true);
      setError(null);

      const response=await fetch('https://deliver.viloud.tv/channel/945813d3a39255b59e505688596ac266/epg?format=xmltv&days_backward=3&days_forward=7');
      
      if (!response.ok) {
        throw new Error('Failed to fetch schedule');
      }

      const xmlData=await response.text();
      const parsedSchedule=parseXMLTV(xmlData);
      setSchedule(parsedSchedule);
      updateCurrentAndNextProgram(parsedSchedule);
    } catch (err) {
      console.error('Error fetching schedule:',err);
      setError('Unable to load schedule. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Update current and next programs based on current time
  const updateCurrentAndNextProgram=(programSchedule=schedule)=> {
    const currentTime=new Date();
    
    const current=programSchedule.find(program=> 
      currentTime >=program.startTime && currentTime <=program.stopTime
    );
    
    const next=programSchedule.find(program=> 
      program.startTime > currentTime
    );

    setCurrentProgram(current);
    setNextProgram(next);

    // Calculate progress for current program
    if (current) {
      const elapsed=currentTime - current.startTime;
      const total=current.stopTime - current.startTime;
      const calculatedProgress=(elapsed / total) * 100;
      setProgress(calculatedProgress);
    }
  };

  // Update time and progress every second
  useEffect(()=> {
    const timer=setInterval(()=> {
      const currentTime=new Date();
      setNow(currentTime);

      // Check if we need to update current/next program
      if (currentProgram && currentTime >=currentProgram.stopTime) {
        updateCurrentAndNextProgram();
      } else if (currentProgram) {
        // Just update progress
        const elapsed=currentTime - currentProgram.startTime;
        const total=currentProgram.stopTime - currentProgram.startTime;
        const calculatedProgress=(elapsed / total) * 100;
        setProgress(calculatedProgress);
      }
    },1000);

    return ()=> clearInterval(timer);
  },[currentProgram]);

  useEffect(()=> {
    fetchSchedule();
    // Refresh schedule every 15 minutes
    const interval=setInterval(fetchSchedule,15 * 60 * 1000);
    return ()=> clearInterval(interval);
  },[]);

  // Format time for display
  const formatTime=(date)=> {
    return date.toLocaleTimeString('en-US',{
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date for display
  const formatDate=(date)=> {
    return date.toLocaleDateString('en-US',{
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get today's schedule
  const getTodaySchedule=()=> {
    const today=new Date();
    today.setHours(0,0,0,0);
    const tomorrow=new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return schedule.filter(program=> 
      program.startTime >=today && program.startTime < tomorrow
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* NYC Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
            alt="New York City Skyline"
            className="w-full h-full object-cover"
          />
          {/* Dark gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-purple-900/80 to-gray-900/90 z-10"></div>
        </div>

        <div className="container mx-auto px-6 relative z-30">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{opacity: 0,y: 50}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
          >
            <motion.div
              className="mb-6"
              initial={{opacity: 0,scale: 0.8}}
              animate={{opacity: 1,scale: 1}}
              transition={{delay: 0.2,duration: 0.8}}
            >
              <img
                src="https://d1zviajkun9gxg.cloudfront.net/user/prod/2024/10/20/fastpages-180d0886-48c7-48c8-8375-98ddef9c0cf6.png"
                alt="LRCS Live TV"
                className="h-24 md:h-32 lg:h-40 mx-auto mb-4"
              />
            </motion.div>
            <motion.p
              className="text-xl md:text-2xl mb-8 text-gray-300 font-medium"
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              transition={{delay: 0.4,duration: 0.8}}
            >
              Watch Linda Roberts take on scammers 24/7 with live programming and your favorite episodes
            </motion.p>
            <motion.div
              className="flex items-center justify-center gap-6 mb-8"
              initial={{opacity: 0,y: 30}}
              animate={{opacity: 1,y: 0}}
              transition={{delay: 0.6,duration: 0.8}}
            >
              <div className="flex items-center gap-2 bg-red-600 px-4 py-2 rounded-full">
                <SafeIcon icon={FiLive} className="text-white animate-pulse" />
                <span className="text-white font-bold">LIVE NOW</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiEye} />
                <span>24/7 Streaming</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Live Player Section */}
      <section className="py-12 bg-gray-800">
        <div className="container mx-auto px-6">
          <motion.div
            className="max-w-6xl mx-auto"
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            <div className="bg-gray-900 p-8 rounded-2xl border border-[#ffc700]/30 shadow-2xl">
              <div className="aspect-video w-full rounded-xl overflow-hidden bg-black">
                <div
                  style={{position: 'relative',paddingBottom: '56.25%',height: 0}}
                  className="w-full"
                >
                  <iframe
                    src="https://player.viloud.tv/embed/channel/945813d3a39255b59e505688596ac266?autoplay=0&volume=1&controls=1&title=1&share=1&open_playlist=0&random=0"
                    style={{position: 'absolute',top: 0,left: 0,width: '100%',height: '100%'}}
                    frameBorder="0"
                    allow="autoplay"
                    allowFullScreen
                    title="LRCS Live TV Stream"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Now & Next Section */}
      <section className="py-16 bg-gradient-to-br from-purple-900 to-gray-900">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#ffc700] to-cyan-400 bg-clip-text text-transparent">
              Now & Next
            </h2>
            <button
              onClick={fetchSchedule}
              className="bg-gradient-to-r from-[#ffc700] to-[#ffb700] hover:from-[#ffb700] hover:to-[#ffa700] text-black px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 transform hover:scale-105 flex items-center gap-2 mx-auto"
            >
              <SafeIcon icon={FiRefreshCw} className={`text-lg ${loading ? 'animate-spin' : ''}`} />
              Refresh Schedule
            </button>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Now Playing */}
            <motion.div
              className="bg-gray-800 p-8 rounded-2xl border border-red-500/50"
              initial={{opacity: 0,x: -50}}
              whileInView={{opacity: 1,x: 0}}
              transition={{duration: 0.8}}
              viewport={{once: true}}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-red-600 p-3 rounded-full">
                  <SafeIcon icon={FiLive} className="text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Now Playing</h3>
                  <div className="flex items-center gap-2 text-red-400">
                    <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium">LIVE</span>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-700 rounded mb-3"></div>
                  <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                </div>
              ) : currentProgram ? (
                <div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-20 h-20 bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                      {currentProgram.iconUrl ? (
                        <img
                          src={currentProgram.iconUrl}
                          alt={currentProgram.title}
                          className="w-full h-full object-cover"
                          onError={(e)=> {
                            e.target.onerror=null;
                            e.target.src="https://s3.us-east-1.wasabisys.com/lrcstv/CDN/LRCS-web-Logo";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-800">
                          <SafeIcon icon={FiImage} className="text-2xl text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-white mb-1">{currentProgram.title}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-2">
                        <div className="flex items-center gap-2">
                          <SafeIcon icon={FiClock} />
                          <span>{formatTime(currentProgram.startTime)} - {formatTime(currentProgram.stopTime)}</span>
                        </div>
                      </div>
                      {currentProgram.description && (
                        <p className="text-gray-300 text-sm line-clamp-2">{currentProgram.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Started: {formatTime(currentProgram.startTime)}</span>
                      <span>Ends: {formatTime(currentProgram.stopTime)}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-red-500 to-[#ffc700] rounded-full"
                        style={{width: `${progress}%`}}
                      ></div>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-gray-400">
                        {Math.floor((now - currentProgram.startTime) / (1000 * 60))} min elapsed
                      </span>
                      <span className="text-xs text-gray-400">
                        {Math.floor((currentProgram.stopTime - now) / (1000 * 60))} min remaining
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <SafeIcon icon={FiTv} className="text-4xl text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">No current program information available</p>
                </div>
              )}
            </motion.div>

            {/* Next Up */}
            <motion.div
              className="bg-gray-800 p-8 rounded-2xl border border-[#ffc700]/50"
              initial={{opacity: 0,x: 50}}
              whileInView={{opacity: 1,x: 0}}
              transition={{duration: 0.8}}
              viewport={{once: true}}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-[#ffc700] p-3 rounded-full">
                  <SafeIcon icon={FiPlay} className="text-xl text-black" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Next Up</h3>
                  <span className="text-[#ffc700] text-sm font-medium">Coming Soon</span>
                </div>
              </div>

              {loading ? (
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-700 rounded mb-3"></div>
                  <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                </div>
              ) : nextProgram ? (
                <div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-20 h-20 bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                      {nextProgram.iconUrl ? (
                        <img
                          src={nextProgram.iconUrl}
                          alt={nextProgram.title}
                          className="w-full h-full object-cover"
                          onError={(e)=> {
                            e.target.onerror=null;
                            e.target.src="https://s3.us-east-1.wasabisys.com/lrcstv/CDN/LRCS-web-Logo";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-800">
                          <SafeIcon icon={FiImage} className="text-2xl text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-white mb-1">{nextProgram.title}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-2">
                        <div className="flex items-center gap-2">
                          <SafeIcon icon={FiClock} />
                          <span>{formatTime(nextProgram.startTime)} - {formatTime(nextProgram.stopTime)}</span>
                        </div>
                      </div>
                      {nextProgram.description && (
                        <p className="text-gray-300 text-sm line-clamp-2">{nextProgram.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Starts in: {Math.floor((nextProgram.startTime - now) / (1000 * 60))} minutes</span>
                      <span>Duration: {nextProgram.duration} min</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <SafeIcon icon={FiMonitor} className="text-gray-400" />
                      <span className="text-sm text-gray-300">Starts at {formatTime(nextProgram.startTime)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <SafeIcon icon={FiCalendar} className="text-4xl text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">No upcoming program information available</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Today's Schedule */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#ffc700] to-cyan-400 bg-clip-text text-transparent">
              Today's Schedule
            </h2>
            <p className="text-xl text-gray-300">
              {formatDate(new Date())} Programming
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {loading ? (
              <div className="space-y-4">
                {[...Array(6)].map((_,i)=> (
                  <div key={i} className="bg-gray-900 p-6 rounded-xl border border-gray-700 animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-gray-700 rounded-lg"></div>
                      <div className="flex-1">
                        <div className="h-5 bg-gray-700 rounded mb-2"></div>
                        <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <SafeIcon icon={FiRadio} className="text-6xl text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 text-lg mb-4">{error}</p>
                <button
                  onClick={fetchSchedule}
                  className="bg-gradient-to-r from-[#ffc700] to-[#ffb700] hover:from-[#ffb700] hover:to-[#ffa700] text-black px-6 py-3 rounded-full font-bold transition-all duration-300 transform hover:scale-105"
                >
                  Try Again
                </button>
              </div>
            ) : getTodaySchedule().length > 0 ? (
              <div className="space-y-4">
                {getTodaySchedule().map((program,index)=> {
                  const isCurrentProgram=currentProgram && currentProgram.id===program.id;
                  const isNextProgram=nextProgram && nextProgram.id===program.id;

                  return (
                    <motion.div
                      key={program.id}
                      className={`bg-gray-900 p-6 rounded-xl border transition-all duration-300 ${
                        isCurrentProgram 
                          ? 'border-red-500 bg-red-500/5' 
                          : isNextProgram 
                            ? 'border-[#ffc700] bg-[#ffc700]/5' 
                            : 'border-gray-700 hover:border-gray-600'
                      }`}
                      initial={{opacity: 0,y: 20}}
                      whileInView={{opacity: 1,y: 0}}
                      transition={{delay: index * 0.1,duration: 0.6}}
                      viewport={{once: true}}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
                          {program.iconUrl ? (
                            <img
                              src={program.iconUrl}
                              alt={program.title}
                              className="w-full h-full object-cover"
                              onError={(e)=> {
                                e.target.onerror=null;
                                e.target.src="https://s3.us-east-1.wasabisys.com/lrcstv/CDN/LRCS-web-Logo";
                              }}
                            />
                          ) : (
                            <div className={`w-full h-full flex items-center justify-center ${
                              isCurrentProgram ? 'bg-red-600' : isNextProgram ? 'bg-[#ffc700]' : 'bg-purple-600'
                            }`}>
                              <SafeIcon 
                                icon={isCurrentProgram ? FiLive : FiTv} 
                                className={`text-xl ${isNextProgram ? 'text-black' : 'text-white'}`} 
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-white">{program.title}</h3>
                            {isCurrentProgram && (
                              <span className="bg-red-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                                LIVE NOW
                              </span>
                            )}
                            {isNextProgram && (
                              <span className="bg-[#ffc700] text-black px-2 py-1 rounded-full text-xs font-bold">
                                NEXT UP
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-400 mb-2">
                            <div className="flex items-center gap-2">
                              <SafeIcon icon={FiClock} />
                              <span>{formatTime(program.startTime)} - {formatTime(program.stopTime)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <SafeIcon icon={FiMonitor} />
                              <span>{program.duration} min</span>
                            </div>
                          </div>
                          {program.description && (
                            <p className="text-gray-300 text-sm line-clamp-2">{program.description}</p>
                          )}

                          {/* Progress bar for current program */}
                          {isCurrentProgram && (
                            <div className="mt-3">
                              <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-red-500 to-[#ffc700] rounded-full"
                                  style={{width: `${progress}%`}}
                                ></div>
                              </div>
                              <div className="flex justify-between mt-1">
                                <span className="text-xs text-gray-500">
                                  {Math.floor((now - program.startTime) / (1000 * 60))} min
                                </span>
                                <span className="text-xs text-gray-500">
                                  {Math.floor((program.stopTime - now) / (1000 * 60))} min left
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <SafeIcon icon={FiCalendar} className="text-6xl text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No schedule available for today</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LiveTVPage;